// The Edit chat's frame (TOM-527): the board page's edit-mode Neo panel, the
// float of TOM-415, drags by its header and resizes from its edges and
// corners. It stays inside the viewport, at least 320×360 and at most the
// viewport less 16 px. Its position and size are kept per browser. A double
// click on the header puts it back where it starts, at the bottom right. The
// header's "Move or resize" menu does all of it from the keyboard.
//
// Until the owner moves it, the panel has no frame of its own and the CSS
// places it (neo-panel.css). Home's main chat (NeoHomeChat) has none of this.

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

export interface EditChatRect {
  x: number;
  y: number;
  w: number;
  /** Null until resized: as tall as its thread, up to 640 px (neo-panel.css). */
  h: number | null;
}

type SizedRect = EditChatRect & { h: number };

export const EDIT_CHAT_MIN_WIDTH = 320;
export const EDIT_CHAT_MIN_HEIGHT = 360;
/** The viewport less 16 px: 8 px clear on each side. */
const EDGE = 8;
/** One step of the keyboard menu. */
const STEP = 40;
const STORAGE_KEY = 'tombolo:edit-chat-frame';

type Edge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
const EDGES: readonly Edge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

/**
 * Fits a frame to a viewport: the size first, then the position. A frame with
 * no height of its own is placed by `shownHeight`, the height it is drawn at.
 */
export function clampEditChatRect(
  rect: EditChatRect,
  viewport: { width: number; height: number },
  shownHeight = 0
): EditChatRect {
  const maxW = Math.max(0, viewport.width - 2 * EDGE);
  const maxH = Math.max(0, viewport.height - 2 * EDGE);
  const w = Math.min(Math.max(rect.w, EDIT_CHAT_MIN_WIDTH), maxW);
  const h = rect.h === null ? null : Math.min(Math.max(rect.h, EDIT_CHAT_MIN_HEIGHT), maxH);
  const tall = h ?? Math.min(shownHeight, maxH);
  const x = Math.min(Math.max(rect.x, EDGE), viewport.width - EDGE - w);
  const y = Math.min(Math.max(rect.y, EDGE), viewport.height - EDGE - tall);
  return {
    x: Math.round(x),
    y: Math.round(y),
    w: Math.round(w),
    h: h === null ? null : Math.round(h),
  };
}

/**
 * The frame a resize from `edge` gives, moved `dx`, `dy` from `start`. The
 * opposite edge holds still, also when the size stops at its minimum or maximum.
 */
export function resizeEditChatRect(
  start: SizedRect,
  edge: Edge,
  dx: number,
  dy: number,
  viewport: { width: number; height: number }
): EditChatRect {
  const maxW = Math.max(0, viewport.width - 2 * EDGE);
  const maxH = Math.max(0, viewport.height - 2 * EDGE);
  const fitW = (w: number) => Math.min(Math.max(w, EDIT_CHAT_MIN_WIDTH), maxW);
  const fitH = (h: number) => Math.min(Math.max(h, EDIT_CHAT_MIN_HEIGHT), maxH);
  let { x, y, w, h } = start;
  if (edge.includes('e')) w = fitW(Math.min(start.w + dx, viewport.width - EDGE - start.x));
  if (edge.includes('w')) {
    w = fitW(Math.min(start.w - dx, start.x + start.w - EDGE));
    x = start.x + start.w - w;
  }
  if (edge.includes('s')) h = fitH(Math.min(start.h + dy, viewport.height - EDGE - start.y));
  if (edge.includes('n')) {
    h = fitH(Math.min(start.h - dy, start.y + start.h - EDGE));
    y = start.y + start.h - h;
  }
  return clampEditChatRect({ x, y, w, h }, viewport);
}

function viewportSize(): { width: number; height: number } {
  return { width: window.innerWidth, height: window.innerHeight };
}

function readStored(): EditChatRect | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const value = JSON.parse(raw) as Partial<EditChatRect>;
    const { x, y, w, h } = value;
    const finite = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n);
    if (finite(x) && finite(y) && finite(w) && (h === null || finite(h))) return { x, y, w, h };
  } catch {
    // Storage blocked or the value unreadable: the panel starts where the CSS puts it.
  }
  return null;
}

function writeStored(rect: EditChatRect | null): void {
  try {
    if (rect === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, JSON.stringify(rect));
  } catch {
    // Storage blocked: the frame holds for this page only.
  }
}

export interface EditChatFrame {
  /** The panel's inline position and size, once moved. */
  style: CSSProperties | undefined;
  /** Spread on the panel's header: drag, and double click to reset. */
  headerProps: {
    onPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
    onDoubleClick: (event: ReactMouseEvent<HTMLElement>) => void;
    'data-drag-handle': true;
  };
  /** The eight resize handles, as the panel's last children. */
  handles: ReactNode;
  /** The header's "Move or resize" menu. */
  menu: ReactNode;
}

/** Interactive elements in the header keep their own pointer presses. */
function startsOnControl(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('button, a, input, [role="menu"]') !== null;
}

export function useEditChatFrame(
  active: boolean,
  panelRef: RefObject<HTMLElement | null>
): EditChatFrame {
  const [rect, setRect] = useState<EditChatRect | null>(null);
  const rectRef = useRef<EditChatRect | null>(null);
  rectRef.current = rect;

  /** The height the panel is drawn at now. */
  const shownHeight = useCallback(
    () => panelRef.current?.getBoundingClientRect().height ?? 0,
    [panelRef]
  );

  // Opened: the frame this browser kept, fitted to the window as it is now.
  useLayoutEffect(() => {
    if (!active) return;
    const stored = readStored();
    setRect(stored === null ? null : clampEditChatRect(stored, viewportSize(), shownHeight()));
  }, [active, shownHeight]);

  // A smaller window pulls the panel back inside it.
  useEffect(() => {
    if (!active) return undefined;
    const onResize = () => {
      const current = rectRef.current;
      if (current !== null) setRect(clampEditChatRect(current, viewportSize(), shownHeight()));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [active, shownHeight]);

  /** The panel's frame now: its own, or where the CSS put it. */
  const currentRect = useCallback((): EditChatRect | null => {
    if (rectRef.current !== null) return rectRef.current;
    const box = panelRef.current?.getBoundingClientRect();
    if (box === undefined) return null;
    return { x: box.left, y: box.top, w: box.width, h: null };
  }, [panelRef]);

  /** The frame with the height it is drawn at, for a resize to start from. */
  const sized = useCallback(
    (r: EditChatRect): SizedRect => ({ ...r, h: r.h ?? shownHeight() }),
    [shownHeight]
  );

  const commit = useCallback(
    (next: EditChatRect | null) => {
      const fitted = next === null ? null : clampEditChatRect(next, viewportSize(), shownHeight());
      setRect(fitted);
      writeStored(fitted);
    },
    [shownHeight]
  );

  /** Follows one pointer from `event` until it lifts, then keeps the frame. */
  const track = useCallback(
    (event: ReactPointerEvent<HTMLElement>, frameAt: (dx: number, dy: number) => EditChatRect) => {
      const target = event.currentTarget;
      const pointerId = event.pointerId;
      const startX = event.clientX;
      const startY = event.clientY;
      try {
        target.setPointerCapture?.(pointerId);
      } catch {
        // A pointer the browser no longer tracks: the moves still reach the target.
      }
      document.body.classList.add('edit-chat-dragging');
      // The frame of the last move, kept here: a pointerup that follows its
      // move before React renders would find the render's frame one move
      // behind, and the panel reopened there (TOM-527).
      let last: EditChatRect | null = null;
      const onMove = (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return;
        last = frameAt(e.clientX - startX, e.clientY - startY);
        setRect(last);
      };
      const onUp = (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return;
        target.removeEventListener('pointermove', onMove);
        target.removeEventListener('pointerup', onUp);
        target.removeEventListener('pointercancel', onUp);
        document.body.classList.remove('edit-chat-dragging');
        if (last !== null) writeStored(last);
      };
      target.addEventListener('pointermove', onMove);
      target.addEventListener('pointerup', onUp);
      target.addEventListener('pointercancel', onUp);
    },
    []
  );

  const onHeaderPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0 || startsOnControl(event.target)) return;
      const start = currentRect();
      if (start === null) return;
      const tall = shownHeight();
      event.preventDefault();
      track(event, (dx, dy) =>
        clampEditChatRect({ ...start, x: start.x + dx, y: start.y + dy }, viewportSize(), tall)
      );
    },
    [currentRect, shownHeight, track]
  );

  const onHeaderDoubleClick = useCallback(
    (event: ReactMouseEvent<HTMLElement>) => {
      if (startsOnControl(event.target)) return;
      commit(null);
    },
    [commit]
  );

  const onHandlePointerDown = useCallback(
    (edge: Edge, event: ReactPointerEvent<HTMLElement>) => {
      if (event.button !== 0) return;
      const start = currentRect();
      if (start === null) return;
      event.preventDefault();
      event.stopPropagation();
      const from = sized(start);
      track(event, (dx, dy) => resizeEditChatRect(from, edge, dx, dy, viewportSize()));
    },
    [currentRect, sized, track]
  );

  const nudge = useCallback(
    (change: (r: EditChatRect) => EditChatRect) => {
      const start = currentRect();
      if (start !== null) commit(change(start));
    },
    [currentRect, commit]
  );

  const style: CSSProperties | undefined =
    !active || rect === null
      ? undefined
      : {
          left: rect.x,
          top: rect.y,
          width: rect.w,
          right: 'auto',
          bottom: 'auto',
          ...(rect.h === null
            ? // As tall as its thread, up to 640 px, and never past the window's foot.
              { height: 'auto', maxHeight: `min(640px, calc(100dvh - ${rect.y + EDGE}px))` }
            : { height: rect.h, maxHeight: 'none' }),
        };

  const handles = active ? (
    <>
      {EDGES.map((edge) => (
        <div
          key={edge}
          className={`edit-chat-handle edit-chat-handle--${edge}`}
          data-edge={edge}
          aria-hidden="true"
          onPointerDown={(event) => onHandlePointerDown(edge, event)}
        />
      ))}
    </>
  ) : null;

  const menu = active ? (
    <EditChatMenu
      frame={rect}
      onMove={(dx, dy) => nudge((r) => ({ ...r, x: r.x + dx, y: r.y + dy }))}
      onResize={(dw, dh) =>
        nudge((r) => resizeEditChatRect(sized(r), 'se', dw, dh, viewportSize()))
      }
      onReset={() => commit(null)}
    />
  ) : null;

  return {
    style,
    headerProps: {
      onPointerDown: onHeaderPointerDown,
      onDoubleClick: onHeaderDoubleClick,
      'data-drag-handle': true,
    },
    handles,
    menu,
  };
}

const MENU_ITEMS: readonly {
  key: string;
  label: string;
  run: (m: EditChatMenuActions) => void;
}[] = [
  { key: 'left', label: 'Move left', run: (m) => m.onMove(-STEP, 0) },
  { key: 'right', label: 'Move right', run: (m) => m.onMove(STEP, 0) },
  { key: 'up', label: 'Move up', run: (m) => m.onMove(0, -STEP) },
  { key: 'down', label: 'Move down', run: (m) => m.onMove(0, STEP) },
  { key: 'wider', label: 'Wider', run: (m) => m.onResize(STEP, 0) },
  { key: 'narrower', label: 'Narrower', run: (m) => m.onResize(-STEP, 0) },
  { key: 'taller', label: 'Taller', run: (m) => m.onResize(0, STEP) },
  { key: 'shorter', label: 'Shorter', run: (m) => m.onResize(0, -STEP) },
];

interface EditChatMenuActions {
  onMove: (dx: number, dy: number) => void;
  onResize: (dw: number, dh: number) => void;
  onReset: () => void;
}

/**
 * The keyboard way to do what dragging does. Move and resize items keep the
 * menu open so they can be pressed again; Reset position closes it.
 */
function EditChatMenu({
  frame,
  ...actions
}: EditChatMenuActions & {
  /** The panel's frame: the menu follows its button when the panel moves. */
  frame: EditChatRect | null;
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [place, setPlace] = useState<CSSProperties>({});

  // The menu is drawn on <body>: inside the panel, its overflow clipped the
  // menu's foot. It opens below the button, or above it where there is more room.
  useLayoutEffect(() => {
    if (!open) return;
    const button = buttonRef.current?.getBoundingClientRect();
    if (button === undefined) return;
    const right = window.innerWidth - button.right;
    const below = window.innerHeight - button.bottom;
    const next: CSSProperties =
      below >= button.top
        ? { top: button.bottom + 6, right, maxHeight: below - 6 - EDGE }
        : { bottom: window.innerHeight - button.top + 6, right, maxHeight: button.top - 6 - EDGE };
    setPlace((prev) =>
      prev.top === next.top && prev.bottom === next.bottom && prev.right === next.right
        ? prev
        : next
    );
  }, [open, frame]);

  useEffect(() => {
    if (!open) return undefined;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node;
      if (wrapRef.current?.contains(target) === true) return;
      if (menuRef.current?.contains(target) === true) return;
      setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  function close() {
    setOpen(false);
    buttonRef.current?.focus();
  }

  // Escape closes the menu, not the panel: the key stops here, before the
  // panel's own listener on the document.
  function onMenuKeyDown(event: ReactKeyboardEvent<HTMLDivElement>) {
    const items = [...(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const index = items.indexOf(document.activeElement as HTMLElement);
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      items[(index + step + items.length) % items.length]?.focus();
    } else if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      (event.key === 'Home' ? items[0] : items[items.length - 1])?.focus();
    } else if (event.key === 'Tab') {
      setOpen(false);
    }
  }

  return (
    <div className="edit-chat-menu" ref={wrapRef}>
      <button
        ref={buttonRef}
        type="button"
        className="neo-icon-button"
        aria-label="Move or resize"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Move or resize"
        onClick={() => setOpen((value) => !value)}
      >
        <MoveIcon />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="tb-menu edit-chat-menu__list"
              role="menu"
              aria-label="Move or resize"
              style={place}
              onKeyDown={onMenuKeyDown}
            >
              {MENU_ITEMS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  className="tb-menu__item"
                  onClick={() => item.run(actions)}
                >
                  {item.label}
                </button>
              ))}
              <hr className="tb-menu__sep" />
              <button
                type="button"
                role="menuitem"
                className="tb-menu__item"
                onClick={() => {
                  actions.onReset();
                  close();
                }}
              >
                Reset position
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}

// Lucide move.
function MoveIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 2v20M2 12h20M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3" />
    </svg>
  );
}
